/* eslint-disable react/no-array-index-key */
import { FC } from 'react';
import { Link } from 'react-router-dom';
import { useTranslate } from 'hooks/useTranslate';
import { formatCurrency } from 'utils/formats';
import {
  PartnerAgreedTradingOffer,
  TradingOffer,
} from '../../../types/TradingOffer';

type TradingOperationsCardProps = {
  operations: PartnerAgreedTradingOffer[];
  activeCount: number;
  totalCount: number;
  societyId: string;
  isButtonVariant?: boolean;
};

const TradingOperationsCard: FC<TradingOperationsCardProps> = ({
  operations,
  activeCount,
  totalCount,
  societyId,
  isButtonVariant = false,
}) => {
  const { t } = useTranslate();

  const recentOperations = operations.slice(0, 2);

  const getOperationDescription = (operation: PartnerAgreedTradingOffer) => {
    const tradingOffer = operation.tradingOffer as TradingOffer;
    const type = tradingOffer?.type === 'SELL' ? t('Sale') : t('Purchase');
    const shareClass = tradingOffer?.shareClass || '';
    const pricePerShare = tradingOffer?.sharePrice || 0;

    return `${type} ${shareClass} - ${formatCurrency(pricePerShare)}/${t(
      'Share'
    ).toLowerCase()}`;
  };

  const getOperationAmount = (operation: PartnerAgreedTradingOffer) => {
    const tradingOffer = operation.tradingOffer as TradingOffer;
    const sharesCount = operation.sharesCount?.agreed || 0;
    const pricePerShare = tradingOffer?.sharePrice || 0;

    return formatCurrency(sharesCount * pricePerShare);
  };

  const linkPath = `/trading-operations/${societyId}`;

  return (
    <div className="card card-full h-100">
      <div className="card-inner d-flex flex-column h-100">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h6 className="card-title text-base mb-0">{t('MyOperations')}</h6>
          {!isButtonVariant && (
            <Link to={linkPath} className="link link-soft is-disable">
              {t('ViewAll')}
            </Link>
          )}
        </div>

        <div className="card-amount mb-2">
          <span className="amount">{totalCount}</span>
          {activeCount > 0 && (
            <span className="text-small ml-1">
              / {activeCount.toString().padStart(2, '0')} {t('actives')}
            </span>
          )}
        </div>

        <div className="flex-grow-1">
          {recentOperations.length > 0 ? (
            <div className="d-flex flex-column gap-1">
              {recentOperations.map((operation, index) => (
                <div
                  key={`operation-${index}`}
                  className="d-flex justify-content-between align-items-center"
                >
                  <span className="sub-text text-ellipsis">
                    {getOperationDescription(operation)}
                  </span>
                  <span className="lead-text text-nowrap ml-2">
                    {getOperationAmount(operation)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-soft text-center py-2">
              {t('NoOperationsYet')}
            </div>
          )}
        </div>

        {isButtonVariant && (
          <Link to={linkPath} className="btn btn-light btn-block mt-3">
            {t('ViewAllMyOperations')}
          </Link>
        )}
      </div>
    </div>
  );
};

export default TradingOperationsCard;
